import { useEffect, useState } from 'react';
import Section from '../shared/Section';
import Btn from '../shared/Btn';
import { findStepIndexByField } from '../STEPS';
import { listClaimDocuments } from '../../../api/claimsClient';

// Representative wire field for each row, used by Edit-link → step jump.
const STEP_FIELD = {
  patient: 'patient_id',
  subscriber: 'insured.member_id',
  payer: 'payer.payer_id',
  provider: 'billing_provider.npi',
  diagnosis: 'diagnoses.0.diagnosis_code',
  lines: 'service_lines.0.cpt_hcpcs_code',
};
const DOCS_STEP_IDX = 4;

/**
 * @param {{ k: string, val: any, onEdit?: (() => void) | null }} props
 */
const Row = ({ k, val, onEdit }) => (
  <div style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--ab-border-hair)' }}>
    <span style={{
      fontSize: 12, color: 'var(--ab-fg-subtle)', width: 160, flexShrink: 0,
      textTransform: 'uppercase', letterSpacing: 'var(--ab-tracking-label)',
      fontWeight: 'var(--ab-weight-semi)',
    }}>{k}</span>
    <span style={{ flex: 1, fontSize: 14, color: val ? 'var(--ab-fg)' : 'var(--ab-fg-subtle)' }}>
      {val || '—'}
    </span>
    {onEdit && (
      <button
        type="button" onClick={onEdit}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 'var(--ab-weight-semi)' }}
      >
        Edit
      </button>
    )}
  </div>
);

/**
 * @param {{
 *   claim: any,
 *   editable: boolean,
 *   onJumpToStep: (idx: number) => void,
 *   onPreview: () => void,
 *   onSubmit: () => void,
 *   validationErrorCount: number,
 *   documentsRefreshKey?: number,
 * }} props
 */
export default function ReviewSection({ claim, editable, onJumpToStep, onPreview, onSubmit, validationErrorCount, documentsRefreshKey = 0 }) {
  const patient = claim.patient;
  const lines = claim.service_lines || [];
  const total = lines.reduce((/** @type {number} */ s, /** @type {any} */ l) => s + (Number(l.charge_amount) || 0), 0);
  const dxCodes = (claim.diagnoses || [])
    .filter((/** @type {any} */ d) => d?.diagnosis_code)
    .map((/** @type {any} */ d) => d.diagnosis_code).join(', ');

  // The claim read payload has no supporting_documents — the list lives behind
  // GET /claims/{id}/documents/, the same source DocumentsSection reads. Counting
  // the absent field always said "0 attachments". Null until loaded, so an
  // unknown count renders as a dash rather than a confident zero.
  const [docCount, setDocCount] = useState(/** @type {number|null} */ (null));
  useEffect(() => {
    if (!claim?.id) return undefined;
    let cancelled = false;
    listClaimDocuments(claim.id)
      .then((list) => { if (!cancelled) setDocCount(Array.isArray(list) ? list.length : 0); })
      .catch(() => { if (!cancelled) setDocCount(null); });
    return () => { cancelled = true; };
  }, [claim?.id, documentsRefreshKey]);
  const dateOfService = claim.service_lines?.[0]?.service_date_from || claim.service_date_from;

  /** @param {keyof typeof STEP_FIELD} key */
  const goto = (key) => () => {
    if (!editable) return;
    const i = findStepIndexByField(STEP_FIELD[key]);
    if (i >= 0) onJumpToStep(i);
  };
  const gotoDocs = () => { if (editable) onJumpToStep(DOCS_STEP_IDX); };

  return (
    <Section icon="eye" title={editable ? 'Review Claim' : 'Submitted Claim'} subtitle="Confirm before submitting">
      <Row k="Patient" val={patient ? `${patient.first_name} ${patient.last_name}` : ''} onEdit={editable ? goto('patient') : null} />
      <Row k="DOB" val={patient?.date_of_birth} />
      <Row k="Date of Service" val={dateOfService} onEdit={editable ? goto('patient') : null} />
      <Row k="Place of Service" val={claim.place_of_service} />
      <Row k="Payer" val={claim.payer_snapshot?.name} onEdit={editable ? goto('payer') : null} />
      <Row k="Member ID" val={claim.insured?.member_id} onEdit={editable ? goto('subscriber') : null} />
      <Row k="Billing Provider" val={claim.billing_provider_snapshot?.organization_name} onEdit={editable ? goto('provider') : null} />
      <Row k="Billing NPI" val={claim.billing_provider_snapshot?.npi} />
      <Row k="Diagnoses" val={dxCodes} onEdit={editable ? goto('diagnosis') : null} />
      <Row k="Service Lines" val={`${lines.length} line${lines.length === 1 ? '' : 's'}`} onEdit={editable ? goto('lines') : null} />
      <Row k="Total Charges" val={`$${total.toFixed(2)}`} />
      <Row k="Documents" val={docCount === null ? '' : `${docCount} attachment${docCount === 1 ? '' : 's'}`} onEdit={editable ? gotoDocs : null} />

      <div style={{ display: 'flex', gap: 10, marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--ab-border-hair)' }}>
        <Btn kind="secondary" icon="eye" onClick={onPreview}>Preview CMS-1500</Btn>
        <div style={{ flex: 1 }} />
        {editable && (
          <Btn kind="primary" icon="send" onClick={onSubmit} aria-disabled={validationErrorCount > 0}>
            Submit Claim
          </Btn>
        )}
      </div>
    </Section>
  );
}
